/**
 * sentry-plugins.ts — Canonical ɳSentry plugin registry
 *
 * Source of truth for all 13 ɳSentry plugins.
 * Baseline (7) + Expansion (6, nself-audit FREE tier).
 * Canonical per SPORT F04-PLUGIN-INVENTORY-PRO.md and PPI port registry.
 */

export type PluginTier = 'bundle' | 'free'

export interface SentryPlugin {
  /** Registry slug used in `nself plugin install` */
  slug: string
  /** Display name */
  name: string
  /** One-line description */
  description: string
  /** Plugin category */
  category: 'uptime' | 'status' | 'incidents' | 'alerts' | 'slo' | 'synthetic' | 'rum' | 'errors' | 'cron' | 'oncall' | 'crash' | 'anomaly' | 'audit'
  /** Assigned port per PPI port registry */
  port: number
  /** Which sub-bundle this belongs to */
  group: 'baseline' | 'expansion'
  /** Free tier or requires ɳSentry Bundle license */
  tier: PluginTier
}

export const BASELINE_PLUGINS: SentryPlugin[] = [
  {
    slug: 'nself-uptime-monitor',
    name: 'Uptime Monitor',
    description: 'HTTP/TCP uptime checks with incident triggers and multi-region support.',
    category: 'uptime',
    port: 3831,
    group: 'baseline',
    tier: 'bundle',
  },
  {
    slug: 'nself-status-page',
    name: 'Status Page',
    description: 'Public and private status pages served from your own infrastructure.',
    category: 'status',
    port: 3832,
    group: 'baseline',
    tier: 'bundle',
  },
  {
    slug: 'nself-incident-mgmt',
    name: 'Incident Management',
    description: 'Incident lifecycle management with timelines and post-mortems.',
    category: 'incidents',
    port: 3833,
    group: 'baseline',
    tier: 'bundle',
  },
  {
    slug: 'nself-alert-router',
    name: 'Alert Router',
    description: 'Multi-channel alert routing: Slack, email, PagerDuty, and webhooks.',
    category: 'alerts',
    port: 3834,
    group: 'baseline',
    tier: 'bundle',
  },
  {
    slug: 'nself-slo-tracker',
    name: 'SLO Tracker',
    description: 'SLO compliance dashboards and error budget tracking.',
    category: 'slo',
    port: 3835,
    group: 'baseline',
    tier: 'bundle',
  },
  {
    slug: 'nself-synthetic-monitor',
    name: 'Synthetic Monitor',
    description: 'Scripted browser and API workflow checks for proactive uptime testing.',
    category: 'synthetic',
    port: 3836,
    group: 'baseline',
    tier: 'bundle',
  },
  {
    slug: 'nself-rum',
    name: 'Real-User Monitoring',
    description: 'Core Web Vitals, session replay, and front-end performance metrics.',
    category: 'rum',
    port: 3837,
    group: 'baseline',
    tier: 'bundle',
  },
]

export const EXPANSION_PLUGINS: SentryPlugin[] = [
  {
    slug: 'nself-errors',
    name: 'Error Tracking',
    description: 'Error tracking with stack traces, breadcrumbs, and release attribution.',
    category: 'errors',
    port: 3838,
    group: 'expansion',
    tier: 'bundle',
  },
  {
    slug: 'nself-cron-monitor',
    name: 'Cron Monitor',
    description: 'Cron job heartbeat monitoring with missed-run and overdue alerts.',
    category: 'cron',
    port: 3839,
    group: 'expansion',
    tier: 'bundle',
  },
  {
    slug: 'nself-oncall',
    name: 'On-Call',
    description: 'On-call rotations, escalation policies, and paging integrations.',
    category: 'oncall',
    port: 3840,
    group: 'expansion',
    tier: 'bundle',
  },
  {
    slug: 'nself-crash',
    name: 'Crash Reporter',
    description: 'Mobile and desktop crash reporting with symbolication.',
    category: 'crash',
    port: 3841,
    group: 'expansion',
    tier: 'bundle',
  },
  {
    slug: 'nself-anomaly',
    name: 'Anomaly Detection',
    description: 'ML-based anomaly detection on metrics and error rate spikes.',
    category: 'anomaly',
    port: 3842,
    group: 'expansion',
    tier: 'bundle',
  },
  {
    slug: 'nself-audit',
    name: 'Audit Log',
    description: 'Tamper-evident audit log for all admin and infrastructure actions.',
    category: 'audit',
    port: 3843,
    group: 'expansion',
    /** nself-audit is FREE in all tiers per Security-Always-Free Doctrine */
    tier: 'free',
  },
]

/** All 13 ɳSentry plugins in canonical order */
export const ALL_SENTRY_PLUGINS: SentryPlugin[] = [
  ...BASELINE_PLUGINS,
  ...EXPANSION_PLUGINS,
]

export const PLUGIN_COUNTS = {
  baseline: BASELINE_PLUGINS.length,
  expansion: EXPANSION_PLUGINS.length,
  total: ALL_SENTRY_PLUGINS.length,
} as const
